import TerranisBusinessLines from 'library/src/core/models/TerranisBusinessLines';
import {getErrorMessage} from 'library/src/core/services/LogService';
import FieldProcessEngineJob from 'library/src/processing/models/FieldProcessEngineJob';
import ProcessEngineType from 'library/src/processing/models/ProcessEngineType';
import {pixagriProcessingOrchestratorApiService} from 'library/src/processing/services/PixagriProcessingOrchestratorApiService';
import {FieldStatisticRecord} from 'library/src/statistic/models/FieldStatisticRecord';
import wagoStatisticsApiService from 'library/src/statistic/services/WagoStatisticsApiService';
import {all, call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {fetchError, fetchStart, fetchSuccess} from 'shared/actions';
import log from 'shared/services/LogService';
import ExecuteProcessFieldRequest from '../../../library/src/processing/models/ExecuteProcessFieldRequest';
import visioCropActions, {
  EXECUTE_PROCESSING_JOB,
  ExecuteProcessingJobAction,
  GET_STATISTICS,
  GetStatisticsAction,
  LOAD_PROCESS_ENGINE_JOBS,
  LoadProcessEngineJobsAction,
} from '../actions/VisioCropActions';

import {DateTime} from 'luxon';
import {AppState} from 'shared/store';
import VisioCropFertilizationParameters from '../../../library/src/processing/models/visiocrop/VisioCropFertilizationParameters';
import {ProcessExecuteForm, RapeseedFertilizationParameters} from '../models/ProcessExecuteForm';
import {VisioCropState} from '../models/VisioCropState';
export function* visioCropSagas() {
  yield all([
    takeEvery(LOAD_PROCESS_ENGINE_JOBS, loadProcessEngineJobsSaga),
    takeEvery(GET_STATISTICS, getStatisticsSaga),
    takeLatest(EXECUTE_PROCESSING_JOB, executeProcessingJobSaga),
  ]);
}

function getProcessingParameters(
  process: ProcessEngineType,
  form: ProcessExecuteForm,
  statistcs: FieldStatisticRecord[],
): any {
  switch (process) {
    case ProcessEngineType.VC_RAPESEED_FERTILIZATION:
      const formParams = form.parameters as RapeseedFertilizationParameters;
      let ndviEw = '';
      let ndviLw = '';
      if (formParams.ew_stat_id) {
        const ewStat = statistcs.find((stat) => stat.id === formParams.ew_stat_id);
        if (ewStat) {
          ndviEw = ewStat.assets?.analytics?.href || '';
        }
      }
      if (formParams.lw_stat_id) {
        const lwStat = statistcs.find((stat) => stat.id === formParams.lw_stat_id);
        if (lwStat) {
          ndviLw = lwStat.assets?.analytics?.href || '';
        }
      }
      const parameters: VisioCropFertilizationParameters = {
        inputs: {
          values: {
            ndvi_ew: formParams.ew_value,
            ndvi_ew_raster: ndviEw,
            ndvi_lw: formParams.lw_value,
            ndvi_lw_raster: ndviLw,
          },
          override_parameters: {},
        },
        outputs: {},
      };
      return parameters;
    default:
      return null;
  }
}

function* executeProcessingJobSaga(action: ExecuteProcessingJobAction) {
  try {
    yield put(fetchStart('executeProcessingJobSaga'));
    const {token}: {token: string} = yield select((state) => state.auth);
    const {statistics}: VisioCropState = yield select((state: AppState) => state.visioCrop);
    const form = action.payload;
    const {processEngine} = action.payload;
    if (!form.farmer_id || !form.field_id || !form.campaign_id || !processEngine) {
      throw new Error('Farmer ID, Field ID, Campaign ID, and Process Engine are required to execute processing job.');
    }
    const payload: ExecuteProcessFieldRequest = {
      business_line: TerranisBusinessLines.AGRI,
      distributor_ref: 'TERRANIS',
      organization_ref: form.farmer_id.toString(),
      producer_ref: form.farmer_id.toString(),
      field_id: form.field_id,
      campaign_id: form.campaign_id,
      execution_mode: form.execution_mode,
      process_parameters: getProcessingParameters(processEngine, form, Object.values(statistics)),
    };
    yield call(
      pixagriProcessingOrchestratorApiService.executeProcessField.bind(pixagriProcessingOrchestratorApiService),
      token,
      processEngine!,
      payload,
    );
    yield put(visioCropActions.onSelectProcessEngine(processEngine));
    yield put(visioCropActions.loadProcessEngineJobs(processEngine));
    yield put(fetchSuccess('executeProcessingJobSaga'));
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    log.error(`Error in executeProcessingJobSaga: ${errorMessage}`);
    yield put(fetchError('executeProcessingJobSaga', errorMessage));
  }
}
function* getStatisticsSaga(action: GetStatisticsAction) {
  try {
    yield put(fetchStart('getStatisticsSaga'));
    const {token}: {token: string} = yield select((state) => state.auth);
    const {indicator, campaignId, fieldId, processingType} = action.payload;

    const res: FieldStatisticRecord[] = yield call(
      wagoStatisticsApiService.getStatistics.bind(wagoStatisticsApiService),
      token,
      campaignId,
      fieldId,
      processingType,
      indicator,
    );
    const dataMap: {[key: string]: FieldStatisticRecord} = {};
    res.forEach((record) => {
      dataMap[record.ref] = record;
    });
    yield put(visioCropActions.getStatisticsSuccess(dataMap));
    yield put(fetchSuccess('getStatisticsSaga'));
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    log.error(`Error in getStatisticsSaga: ${errorMessage}`);
    yield put(fetchError('getStatisticsSaga', errorMessage));
  }
}
function* loadProcessEngineJobsSaga(action: LoadProcessEngineJobsAction) {
  try {
    yield put(fetchStart('loadProcessEngineJobsSaga'));
    const {token}: {token: string} = yield select((state) => state.auth);
    const {processEngineType} = action.payload;

    const res: FieldProcessEngineJob[] = yield call(
      pixagriProcessingOrchestratorApiService.getFieldProcessEngineJobs.bind(pixagriProcessingOrchestratorApiService),
      token,
      processEngineType,
    );
    const jobsMap: {[key: string]: FieldProcessEngineJob} = {};
    res.forEach((job) => {
      job.date_epoch = DateTime.fromJSDate(job.created_at).toMillis();
      jobsMap[job.id] = job;
    });
    yield put(visioCropActions.loadProcessEngineJobsSuccess(jobsMap));
    yield put(fetchSuccess('loadProcessEngineJobsSaga'));
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    log.error(`Error in loadProcessEngineJobsSaga: ${errorMessage}`);
    yield put(fetchError('loadProcessEngineJobsSaga', errorMessage));
  }
}
